import { ResourceHeading2 } from 'components/theme/Typography/ResourceTypography';
import { AvailableTimeList } from 'features/GuestReschedule/components/AvailableTimeList';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import moment, { Moment } from 'moment';
import {
  RescheduleMessage,
  Label,
} from 'features/GuestReschedule/components/Layout';
import InfoCircleIcon from 'assets/bx-info-circle';
import useGuestReschedule from 'features/GuestReschedule/hooks/useGuestReschedule';
import { useGuestRescheduleSessions } from 'features/GuestReschedule/hooks/useGuestRescheduleSessions';
import { DaySchedule } from 'features/GuestReschedule/types';
import { ResourcesDropdown } from 'features/GuestReschedule/components/ResourcesDropdown';
import { AvailableTimeListSkeleton } from 'features/GuestReschedule/components/AvailableTimeSkeleton';
import { DayPickerPopover } from 'components/molecules/DayPickerPopover';
import { DATE_FORMAT } from 'utils';
import { DateInput } from 'components/atoms/DateInput';

interface TimeSelectorProps {
  readonly times: DaySchedule[];
  readonly defaultResourceId: string;
  readonly resources: { label: string; value: string }[];
  readonly noSessionsAvailable: boolean;
  readonly selectedSlotId: string | null;
  readonly onSelectSlot: (value: string) => void;
  readonly isLoading: boolean;
}

export const TimeSelector = ({
  times,
  defaultResourceId,
  resources,
  noSessionsAvailable,
  selectedSlotId,
  onSelectSlot,
  isLoading,
}: TimeSelectorProps) => {
  const { t } = useTranslation();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const {
    bookingSelected,
    setSelectedCalendarDate,
    selectedCalendarDate,
    setSelectedResourceId,
    selectedResourceId,
  } = useGuestReschedule();

  const [calendarMonth, setCalendarMonth] = useState<Moment>(moment());
  const calendarFrom = calendarMonth
    .clone()
    .startOf('month')
    .format('YYYY-MM-DD');

  const calendarTo = calendarMonth.clone().endOf('month').format('YYYY-MM-DD');

  const { sessions: calendarSessions } = useGuestRescheduleSessions(
    bookingSelected?.bookingId,
    calendarFrom,
    calendarTo,
  );

  const todayStr = moment().format('YYYY-MM-DD');

  const calendarDates = calendarSessions.map((s) => s.date);

  const availableDates = calendarDates.includes(todayStr)
    ? calendarDates
    : [todayStr, ...calendarDates];

  const handleMonthChange = (date: Moment | null) => {
    if (!date) {
      return;
    }

    setCalendarMonth(date);
  };

  const onSelectDate = (date: Date) => {
    setSelectedCalendarDate(moment(date).format(DATE_FORMAT));
    setShowDatePicker(false);
  };

  const isDateBlocked = (date: moment.Moment) => {
    return !availableDates.includes(date.format(DATE_FORMAT));
  };

  const datePickerTrigger = (
    <DateInput
      date={selectedCalendarDate ? moment(selectedCalendarDate).toDate() : null}
      dateFormat="MMMM d"
      placeholder={t('selectDate')}
    />
  );

  return (
    <Wrapper ref={containerRef}>
      <Header>
        <ResourceHeading2 fontSize="21px" className="times">
          {t('availableTimes')}
        </ResourceHeading2>
        <AvailableTimesWrapper>
          {resources.length > 1 && (
            <ResourcesDropdown
              selectedResourceId={selectedResourceId || defaultResourceId}
              options={resources}
              onChange={(r) => setSelectedResourceId(r.value)}
              portalContainer={containerRef.current}
            />
          )}
          <DayPickerPopover
            mode="single"
            required
            asChild
            open={showDatePicker}
            onOpenChange={setShowDatePicker}
            defaultMonth={moment(selectedCalendarDate).toDate()}
            onSelect={onSelectDate}
            selected={
              !isDateBlocked(moment(selectedCalendarDate))
                ? moment(selectedCalendarDate).toDate()
                : undefined
            }
            trigger={datePickerTrigger}
            onNextClick={(date) => handleMonthChange(moment(date))}
            onPrevClick={(date) => handleMonthChange(moment(date))}
            modifiers={{
              disabled: (date) => isDateBlocked(moment(date)),
            }}
            footer={null}
          />
        </AvailableTimesWrapper>
      </Header>
      <TimesSelectorWrapper>
        {isLoading ? (
          <AvailableTimeListSkeleton />
        ) : (
          times.map((day) => (
            <AvailableTimeList
              key={day.date}
              date={day.date}
              slots={day.slots}
              selectedSlotId={selectedSlotId}
              onSelectSlot={onSelectSlot}
            />
          ))
        )}
        {noSessionsAvailable && !isLoading && (
          <RescheduleMessage>
            <InfoCircleIcon
              scale={1}
              color="var(--way-colors-primaryTextColor)"
            />
            <Label>{t('noOtherSessionsAvailableForThisExperience')}</Label>
          </RescheduleMessage>
        )}
      </TimesSelectorWrapper>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-top: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`;

const AvailableTimesWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TimesSelectorWrapper = styled.div``;
